import React from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	DropZoneContainer,
	DropZoneContent,
	DropZoneFiles,
	FileTypeText,
	UploadLinkText,
	UploadText,
} from './styled';

interface Props {
	setUploadedFile: (file: File | null) => void;
	inputId: string;
	className?: string;
}

export const FileUploadComponent: React.FC<Props> = ({ setUploadedFile, inputId, className }) => {
	const tCapManage = useTranslationCapManage();

	const handleFileUpload = (fileOrEvent: File | React.ChangeEvent<HTMLInputElement> | null) => {
		let file: File | null = null;

		if (fileOrEvent === null) {
			file = null;
		} else if (
			'target' in fileOrEvent &&
			fileOrEvent.target.files &&
			fileOrEvent.target.files.length > 0
		) {
			file = fileOrEvent.target.files[0];
		} else if (fileOrEvent instanceof File) {
			file = fileOrEvent;
		}

		setUploadedFile(file);
	};

	const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			const file = e.dataTransfer.files[0];
			if (file.type === 'application/pdf') {
				handleFileUpload(file);
			} else {
				showToastV2({
					title: 'Invalid file type',
					message: 'Only PDF files are allowed',
					state: 'error',
				});
			}
		}
	};

	const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		handleFileUpload(e);
	};

	const preventDefaultHandler = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	return (
		<DropZoneContainer
			isDragging={false}
			onDragOver={preventDefaultHandler}
			onDragLeave={preventDefaultHandler}
			onDrop={handleDrop}
			className={className}
		>
			<input
				type="file"
				id={inputId}
				onChange={handleChange}
				accept=".pdf"
				style={{ display: 'none' }}
			/>
			<label htmlFor={inputId}>
				<DropZoneContent>
					<Icon variant="holdings-upload" size="3xl" />
					<DropZoneFiles>
						<UploadText>
							<UploadLinkText>
								{tCapManage('holdings.event.document_click')}
							</UploadLinkText>{' '}
							{tCapManage('holdings.event.document_drag')}
						</UploadText>
						<FileTypeText>{tCapManage('holdings.event.document_type')}</FileTypeText>
					</DropZoneFiles>
				</DropZoneContent>
			</label>
		</DropZoneContainer>
	);
};
